import React, { useEffect, useState } from 'react';
import {
  Table, Thead, Tbody, Tr, Th, Td, Button,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  useDisclosure, Badge, Flex, Text, Box, HStack, Select, Input, SimpleGrid, useToast,
} from '@chakra-ui/react';
import { DownloadIcon } from '@chakra-ui/icons';
import { FiFileText } from 'react-icons/fi';
import { bitacoraApi, Bitacora, abrirPdfEnNuevaPestana } from '../../../api/serviciosApi';

const AREAS = ['Recoleccion', 'Faenas', 'Herramientas', 'Alumbrado', 'Panteones', 'LimpiezaCamiones', 'Decoracion'];

const AREA_COLORS: Record<string, string> = {
  Recoleccion: 'green',
  Faenas: 'orange',
  Herramientas: 'purple',
  Alumbrado: 'yellow',
  Panteones: 'teal',
  LimpiezaCamiones: 'cyan',
  Decoracion: 'pink',
};

const ESTATUS_COLORS: Record<string, string> = {
  activo: 'green',
  Pendiente: 'yellow',
  'En proceso': 'blue',
  Finalizado: 'green',
  Finalizada: 'green',
  Atendido: 'green',
  Cancelado: 'red',
  Cancelada: 'red',
};

const Field = ({ label, value }: { label: string; value?: React.ReactNode }) => (
  <Box>
    <Text fontSize="xs" color="gray.500" fontWeight="bold">{label}</Text>
    <Text fontSize="sm" whiteSpace="pre-wrap">{value || '—'}</Text>
  </Box>
);

const TablaBitacora: React.FC = () => {
  const toast = useToast();
  const { isOpen: isOpenVer, onOpen: onOpenVer, onClose: onCloseVer } = useDisclosure();

  const [selectedItem, setSelectedItem] = useState<Bitacora | null>(null);
  const [data, setData] = useState<Bitacora[]>([]);
  const [cargando, setCargando] = useState(false);
  const [filtroArea, setFiltroArea] = useState('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchData = async () => {
    setCargando(true);
    try {
      const params: any = {};
      if (filtroArea) params.area = filtroArea;
      if (desde) params.desde = desde;
      if (hasta) params.hasta = hasta;
      const res = await bitacoraApi.list(params);
      setData(res || []);
    } catch {
      setData([]);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [filtroArea, desde, hasta]);

  const datosFiltrados = data.filter((item) => {
    if (!busqueda) return true;
    const q = busqueda.toLowerCase();
    return (
      item.actividad?.toLowerCase().includes(q) ||
      item.responsable?.toLowerCase().includes(q) ||
      item.folio?.toLowerCase().includes(q) ||
      item.tipoActividad?.toLowerCase().includes(q)
    );
  });

  const totalPages = Math.ceil(datosFiltrados.length / itemsPerPage);
  const indexUltimo = currentPage * itemsPerPage;
  const indexPrimero = indexUltimo - itemsPerPage;
  const itemsPagina = datosFiltrados.slice(indexPrimero, indexUltimo);

  const handleVer = (item: Bitacora) => { setSelectedItem(item); onOpenVer(); };

  const handleExport = (format: 'excel' | 'pdf') => {
    const params: any = {};
    if (filtroArea) params.area = filtroArea;
    if (desde) params.desde = desde;
    if (hasta) params.hasta = hasta;
    window.open(bitacoraApi.exportUrl(format, params), '_blank');
  };

  const [generandoPdf, setGenerandoPdf] = useState(false);
  const handleReportePdf = async () => {
    setGenerandoPdf(true);
    try {
      const params: any = {};
      if (filtroArea) params.area = filtroArea;
      if (desde) params.fechaInicio = desde;
      if (hasta) params.fechaFin = hasta;
      const blob = await bitacoraApi.reportePdf(params);
      abrirPdfEnNuevaPestana(blob);
    } catch {
      toast({ title: 'Error', description: 'No se pudo generar el reporte PDF.', status: 'error', duration: 4000 });
    } finally {
      setGenerandoPdf(false);
    }
  };

  const fmt = (v?: string) =>
    v ? new Date(v).toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

  return (
    <Box>
      {/* Barra de herramientas */}
      <Flex mb={4} justify="space-between" align="center" flexWrap="wrap" gap={3}>
        <HStack spacing={3} flexWrap="wrap">
          <Input
            placeholder="Buscar por actividad, responsable o folio..."
            value={busqueda}
            onChange={(e) => { setBusqueda(e.target.value); setCurrentPage(1); }}
            size="sm"
            w={{ base: 'full', md: '250px' }}
          />
          <Select
            placeholder="Filtrar por área"
            value={filtroArea}
            onChange={(e) => { setFiltroArea(e.target.value); setCurrentPage(1); }}
            size="sm"
            w="170px"
          >
            {AREAS.map((a) => <option key={a} value={a}>{a}</option>)}
          </Select>
          <HStack spacing={1}>
            <Text fontSize="xs" color="gray.500">Desde</Text>
            <Input type="date" value={desde} onChange={(e) => { setDesde(e.target.value); setCurrentPage(1); }} size="sm" w="150px" />
          </HStack>
          <HStack spacing={1}>
            <Text fontSize="xs" color="gray.500">Hasta</Text>
            <Input type="date" value={hasta} onChange={(e) => { setHasta(e.target.value); setCurrentPage(1); }} size="sm" w="150px" />
          </HStack>
        </HStack>
        <HStack spacing={2}>
          <Button size="sm" leftIcon={<DownloadIcon />} colorScheme="green" variant="outline" onClick={() => handleExport('excel')}>
            Excel
          </Button>
          <Button size="sm" leftIcon={<DownloadIcon />} colorScheme="red" variant="outline" onClick={() => handleExport('pdf')}>
            PDF
          </Button>
          <Button size="sm" leftIcon={<FiFileText />} bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={handleReportePdf} isLoading={generandoPdf}>
            Reporte PDF
          </Button>
        </HStack>
      </Flex>

      {/* Tabla */}
      <Box overflowX="auto" borderRadius="md" boxShadow="md" bg="white">
        <Table variant="simple" size="sm">
          <Thead bg="#5b182b">
            <Tr>
              <Th color="white" fontSize="xs">Fecha</Th>
              <Th color="white" fontSize="xs">Hora</Th>
              <Th color="white" fontSize="xs">Área</Th>
              <Th color="white" fontSize="xs">Tipo Actividad</Th>
              <Th color="white" fontSize="xs">Actividad</Th>
              <Th color="white" fontSize="xs">Responsable</Th>
              <Th color="white" fontSize="xs">Localidad</Th>
              <Th color="white" fontSize="xs">Estatus</Th>
              <Th color="white" fontSize="xs">Acciones</Th>
            </Tr>
          </Thead>
          <Tbody>
            {cargando ? (
              <Tr><Td colSpan={9} textAlign="center" py={8}><Text color="gray.500">Cargando...</Text></Td></Tr>
            ) : itemsPagina.length === 0 ? (
              <Tr><Td colSpan={9} textAlign="center" py={8}><Text color="gray.400">No se encontraron registros en la bitácora</Text></Td></Tr>
            ) : (
              itemsPagina.map((item, idx) => (
                <Tr key={item.id} bg={idx % 2 === 0 ? 'white' : 'gray.50'} _hover={{ bg: 'red.50' }}>
                  <Td>{fmt(item.fecha)}</Td>
                  <Td>{item.hora || '—'}</Td>
                  <Td>
                    <Badge colorScheme={AREA_COLORS[item.area] || 'gray'} fontSize="2xs">{item.area}</Badge>
                  </Td>
                  <Td>{item.tipoActividad || '—'}</Td>
                  <Td maxW="240px"><Text noOfLines={2} fontSize="sm" title={item.actividad}>{item.actividad || '—'}</Text></Td>
                  <Td>{item.responsable || '—'}</Td>
                  <Td>{item.localidad || '—'}</Td>
                  <Td>
                    <Badge colorScheme={ESTATUS_COLORS[item.estatus] || 'gray'} fontSize="xs" px={2} py={0.5} borderRadius="full">
                      {item.estatus}
                    </Badge>
                  </Td>
                  <Td>
                    <Button size="xs" colorScheme="blue" variant="outline" onClick={() => handleVer(item)}>Ver</Button>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>

      {/* Paginación */}
      {totalPages > 1 && (
        <Flex mt={4} justify="space-between" align="center">
          <Text fontSize="sm" color="gray.500">
            Mostrando {indexPrimero + 1}–{Math.min(indexUltimo, datosFiltrados.length)} de {datosFiltrados.length}
          </Text>
          <HStack spacing={1}>
            <Button size="xs" onClick={() => setCurrentPage(1)} isDisabled={currentPage === 1}>«</Button>
            <Button size="xs" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} isDisabled={currentPage === 1}>‹</Button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const page = Math.max(1, currentPage - 2) + i;
              if (page > totalPages) return null;
              return (
                <Button key={page} size="xs" bg={currentPage === page ? '#5b182b' : undefined} color={currentPage === page ? 'white' : undefined} onClick={() => setCurrentPage(page)}>
                  {page}
                </Button>
              );
            })}
            <Button size="xs" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} isDisabled={currentPage === totalPages}>›</Button>
            <Button size="xs" onClick={() => setCurrentPage(totalPages)} isDisabled={currentPage === totalPages}>»</Button>
          </HStack>
        </Flex>
      )}

      {/* Modal: Ver */}
      <Modal isOpen={isOpenVer} onClose={onCloseVer} size="2xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color="#5b182b">
            Detalle de Registro — Bitácora
          </ModalHeader>
          <ModalBody py={5}>
            {selectedItem && (
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                <Field label="FOLIO" value={selectedItem.folio} />
                <Field label="FECHA" value={fmt(selectedItem.fecha)} />
                <Field label="HORA" value={selectedItem.hora} />
                <Field label="ÁREA" value={<Badge colorScheme={AREA_COLORS[selectedItem.area] || 'gray'}>{selectedItem.area}</Badge>} />
                <Field label="TIPO DE ACTIVIDAD" value={selectedItem.tipoActividad} />
                <Field label="ESTATUS" value={<Badge colorScheme={ESTATUS_COLORS[selectedItem.estatus] || 'gray'}>{selectedItem.estatus}</Badge>} />
                <Box gridColumn={{ md: 'span 2' }}>
                  <Field label="ACTIVIDAD" value={selectedItem.actividad} />
                </Box>
                <Field label="RESPONSABLE" value={selectedItem.responsable} />
                <Field label="PERSONAL" value={selectedItem.personal} />
                <Field label="LOCALIDAD" value={selectedItem.localidad} />
                <Field label="COLONIA" value={selectedItem.colonia} />
                <Field label="CALLE" value={selectedItem.calle} />
                <Field label="NÚMERO ECONÓMICO" value={selectedItem.numeroEconomico} />
                <Field label="ENTIDAD ORIGEN" value={`${selectedItem.entidadOrigen} #${selectedItem.entidadOrigenId}`} />
                <Box gridColumn={{ md: 'span 2' }}>
                  <Field label="OBSERVACIONES" value={selectedItem.observaciones} />
                </Box>
              </SimpleGrid>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onCloseVer}>Cerrar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default TablaBitacora;
